import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { getMe, loginUser, registerUser, startSession, endSession } from '../lib/api';
import { persistAuthTokens, clearAuthTokens } from '../lib/api/client';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  onboarding_complete?: boolean;
  onboarding_step?: number;
  profile_updated_at?: string;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  reconnecting: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (data: Record<string, unknown>) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setAuthDirect: (token: string, user: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'dutchdate_token';
const CACHED_USER_KEY = 'dutchdate_user';

function loadCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}
function saveCachedUser(u: User | null): void {
  try {
    if (u) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(CACHED_USER_KEY);
  } catch { /* storage unavailable */ }
}

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  // Hydrate from the last-known user so a valid session renders instantly on
  // cold start (even offline) — we then validate silently in the background.
  const [user, setUser] = useState<User | null>(() =>
    localStorage.getItem(TOKEN_KEY) ? loadCachedUser() : null,
  );
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const sessionRef = useRef<string | null>(null);

  const applyUser = (u: User | null) => {
    setUser(u);
    saveCachedUser(u);
  };
  const clearSession = () => {
    clearAuthTokens();
    saveCachedUser(null);
    setToken(null);
    setUser(null);
  };

  // Start session on login
  useEffect(() => {
    if (user && !sessionRef.current) {
      startSession()
        .then((res) => { sessionRef.current = res.data?.session_id; })
        .catch(() => {});
    }
  }, [user]);

  // Definitive auth failure broadcast by the axios interceptor (a 401 that
  // survived a refresh attempt). This is the ONLY path that logs a user out
  // automatically.
  useEffect(() => {
    const handleExpired = () => { clearSession(); };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  // Cold-start session validation — resilient to transient failures.
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }

    const hasCached = !!loadCachedUser();
    // With a cached user we render the app immediately and validate in the
    // background; without one we show a lightweight "reconnecting" state.
    if (hasCached) setLoading(false);

    let cancelled = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const validate = async () => {
      if (cancelled) return;
      try {
        const res = await getMe();
        if (cancelled) return;
        applyUser(res.data);
        setReconnecting(false);
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        // Definitive auth failure: the interceptor already tried to refresh and
        // the server still says 401 → the session is genuinely dead.
        if (status === 401) {
          clearSession();
          setReconnecting(false);
          setLoading(false);
          return;
        }
        // Transient: offline / no response / 5xx / 403 step-up. NEVER log out
        // while the token is still valid — keep retrying with backoff.
        if (hasCached) {
          setLoading(false); // app already visible from cache; retry quietly
        } else {
          setReconnecting(true); // keep loading true, show reconnecting overlay
        }
        attempt += 1;
        const delay = Math.min(1500 * attempt, 8000);
        retryTimer = setTimeout(validate, delay);
      }
    };

    validate();

    // Re-attempt the moment connectivity / focus returns, so an offline launch
    // drops straight into the app once the network is back.
    const onBackOnline = () => { if (!cancelled) validate(); };
    const onVisible = () => { if (!cancelled && document.visibilityState === 'visible') validate(); };
    window.addEventListener('online', onBackOnline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('online', onBackOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token]);

  const login = async (email: string, password: string): Promise<any> => {
    const res = await loginUser({ email, password });
    const data = res.data;
    // MFA gate: don't persist a session until the second factor is verified.
    if (data?.mfa_required) return data;
    persistAuthTokens(data);
    setToken(data.token);
    applyUser(data.user);
    return data;
  };

  const register = async (data: Record<string, unknown>): Promise<User> => {
    const res = await registerUser(data);
    persistAuthTokens(res.data);
    setToken(res.data.token);
    applyUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.removeAllListeners();
      }
    } catch { /* not on native */ }
    if (sessionRef.current) {
      endSession(sessionRef.current).catch(() => {});
      sessionRef.current = null;
    }
    clearSession();
  };

  const refreshUser = async () => {
    try {
      const res = await getMe();
      applyUser(res.data);
    } catch { /* silent — keep the cached session */ }
  };

  const setAuthDirect = (newToken: string, userData: User) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    applyUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, reconnecting, login, register, logout, refreshUser, setAuthDirect, isAuthenticated: !!user }}>
      {children}
      {reconnecting && !user && (
        <div
          data-testid="auth-reconnecting"
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-white/95 backdrop-blur-sm"
        >
          <div className="h-9 w-9 rounded-full border-[3px] border-dutch-teal/25 border-t-dutch-teal animate-spin" />
          <p className="text-sm font-medium text-neutral-500">Reconnecting…</p>
        </div>
      )}
    </AuthContext.Provider>
  );
};
